// Shared UI component that renders the 4 vegan-categorization sections with
// collapse/expand, bulk actions, section editing, and MenuOptionCards.
//
// API:
//   <MenuCategorizationPanel
//     predictedOptions={predictedOptions}
//     categorization={categorization}   // return value of useMenuCategorization
//     editable                          // enables section rename + item editing
//   />

import React, { Fragment, useState } from "react";
import styled from "styled-components";
import {
  CATEGORY_CONFIG,
  groupItemsBySection,
  sortSectionKeys,
} from "./menuCategorizationConstants";
import {
  SectionHeaderContainer,
  SectionHeaderTitle,
  BulkActionButtons,
  BulkActionButton,
  FooterActionContainer,
  CollapseButton,
  SectionSummary,
  MenuSectionHeader,
  SectionHeaderText,
  SectionHeaderInput,
  SectionEditButton,
  AddItemButton,
} from "./menuCategorizationStyles";
import MenuOptionCard from "./MenuOptionCard";

const FullyVeganBanner = styled.div`
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BannerText = styled.div`
  font-size: 14px;
  color: #e65100;
  flex: 1;
`;

const BannerMatchedText = styled.span`
  font-weight: 600;
`;

const ReclassifyButton = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #e65100;
  background: white;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: #fff8e1;
    border-color: #ffb74d;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MenuCategorizationPanel = ({
  predictedOptions,
  categorization,
  editable = false,
  urlIndexMap,
  veganLegend,
  contributingUrls,
  fullMenuVeganSignal,
  onDismissFullyVegan,
}) => {
  const {
    menuItemCategories,
    collapsedSections,
    editingSection,
    setEditingSection,
    editingSectionValue,
    setEditingSectionValue,
    handleCategoryChange,
    handleBulkCategoryChange,
    toggleSection,
    handleSectionRename,
    handleItemSectionChange,
    handleOptionChange,
    getAvailableSections,
    handleAddItem,
    handleDeleteItem,
    handleDeleteUrl,
  } = categorization;

  const [reclassifying, setReclassifying] = useState(false);

  return (
    <>
      {fullMenuVeganSignal?.detected && (
        <FullyVeganBanner>
          <BannerText>
            Detected as fully vegan restaurant
            {fullMenuVeganSignal.matchedText && (
              <>
                {" "}— matched: <BannerMatchedText>"{fullMenuVeganSignal.matchedText}"</BannerMatchedText>
              </>
            )}
          </BannerText>
          {onDismissFullyVegan && (
            <ReclassifyButton
              type="button"
              onClick={async () => {
                setReclassifying(true);
                try {
                  await onDismissFullyVegan();
                } finally {
                  setReclassifying(false);
                }
              }}
              disabled={reclassifying}
            >
              {reclassifying ? "Reclassifying..." : <><i className="fas fa-flag" style={{ marginRight: "5px" }} />Flag as not fully vegan</>}
            </ReclassifyButton>
          )}
        </FullyVeganBanner>
      )}
      {CATEGORY_CONFIG.map(({ key, color, label, prefix, defaultCategory }) => {
        const items = predictedOptions[key];
        if (!items || items.length === 0) return null;

        const isCollapsed = collapsedSections[key];
        const catVeganCount = items.filter(
          (item) =>
            (menuItemCategories[item.option] || defaultCategory) === "vegan"
        ).length;
        const catNotVeganCount = items.filter(
          (item) =>
            (menuItemCategories[item.option] || defaultCategory) === "notVegan"
        ).length;
        const catMaybeCount = items.length - catVeganCount - catNotVeganCount;

        const isFullyVegan =
          key === "GUARANTEED_VEGAN" &&
          items.some((item) => item.source === "fully_vegan_restaurant");

        return (
          <Fragment key={key}>
            <SectionHeaderContainer color={color}>
              <SectionHeaderTitle color={color}>
                {prefix}{" "}
                {key === "GUARANTEED_VEGAN" && isFullyVegan
                  ? "Fully Vegan Restaurant"
                  : label}{" "}
                ({items.length} items)
              </SectionHeaderTitle>
              <BulkActionButtons>
                <BulkActionButton
                  onClick={() =>
                    handleBulkCategoryChange(items, "vegan", key)
                  }
                  type="button"
                >
                  Mark All as Vegan
                </BulkActionButton>
                <BulkActionButton
                  onClick={() =>
                    handleBulkCategoryChange(items, "notVegan", key)
                  }
                  type="button"
                >
                  Mark All as Not Vegan
                </BulkActionButton>
                <CollapseButton
                  onClick={() => toggleSection(key)}
                  type="button"
                  color={color}
                >
                  {isCollapsed ? "\u25BC Expand" : "\u25B2 Collapse"}
                </CollapseButton>
              </BulkActionButtons>
            </SectionHeaderContainer>
            <SectionSummary>
              {catVeganCount} vegan {"\u2022"} {catMaybeCount} maybe{" "}
              {"\u2022"} {catNotVeganCount} not vegan
            </SectionSummary>

            {!isCollapsed &&
              (() => {
                const { grouped: itemsBySection, sectionOrderMap } =
                  groupItemsBySection(items);
                const sKeys = sortSectionKeys(
                  Object.keys(itemsBySection),
                  sectionOrderMap
                );

                return (
                  <>
                    <div style={{ marginBottom: "12px" }}>
                      {sKeys.map((section) => {
                        const sectionItems = itemsBySection[section];
                        const isEditing =
                          editingSection?.categoryKey === key &&
                          editingSection?.oldName === section;
                        return (
                          <div key={section || "no-section"}>
                            {section &&
                              section !== "null" &&
                              section !== null && (
                                <MenuSectionHeader>
                                  {isEditing ? (
                                    <SectionHeaderInput
                                      value={editingSectionValue}
                                      onChange={(e) =>
                                        setEditingSectionValue(e.target.value)
                                      }
                                      onBlur={() =>
                                        handleSectionRename(
                                          key,
                                          section,
                                          editingSectionValue
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleSectionRename(
                                            key,
                                            section,
                                            editingSectionValue
                                          );
                                        if (e.key === "Escape") {
                                          setEditingSection(null);
                                          setEditingSectionValue("");
                                        }
                                      }}
                                      autoFocus
                                    />
                                  ) : (
                                    <>
                                      <SectionHeaderText>
                                        {section}
                                      </SectionHeaderText>
                                      {editable && (
                                        <SectionEditButton
                                          type="button"
                                          onClick={() => {
                                            setEditingSection({
                                              categoryKey: key,
                                              oldName: section,
                                            });
                                            setEditingSectionValue(section);
                                          }}
                                          title="Rename section"
                                        >
                                          <i className="fas fa-pencil-alt" />
                                        </SectionEditButton>
                                      )}
                                    </>
                                  )}
                                </MenuSectionHeader>
                              )}
                            {sectionItems.map((item) => (
                              <MenuOptionCard
                                key={item.option}
                                option={item.option}
                                description={item.description}
                                section={item.section}
                                originalCategory={key}
                                currentCategory={
                                  menuItemCategories[item.option] ||
                                  defaultCategory
                                }
                                probability={item.probability}
                                onCategoryChange={handleCategoryChange}
                                onOptionChange={(optionName, changes) =>
                                  handleOptionChange(optionName, key, changes)
                                }
                                onSectionChange={(optionName, newSection) =>
                                  handleItemSectionChange(
                                    optionName,
                                    key,
                                    newSection
                                  )
                                }
                                availableSections={getAvailableSections()}
                                editable={editable}
                                onDelete={editable ? (optionName) => handleDeleteItem(optionName, key) : undefined}
                                source={item.source}
                                sourceUrl={item.source_url}
                                urlIndexMap={urlIndexMap}
                                veganLegend={veganLegend}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    <FooterActionContainer color={color}>
                      <BulkActionButton
                        onClick={() =>
                          handleBulkCategoryChange(items, "vegan", key)
                        }
                        type="button"
                        color={color}
                      >
                        Mark All as Vegan
                      </BulkActionButton>
                      <BulkActionButton
                        onClick={() =>
                          handleBulkCategoryChange(items, "notVegan", key)
                        }
                        type="button"
                        color={color}
                      >
                        Mark All as Not Vegan
                      </BulkActionButton>
                    </FooterActionContainer>
                  </>
                );
              })()}
          </Fragment>
        );
      })}
      {editable && (
        <AddItemButton type="button" onClick={handleAddItem}>
          <i className="fas fa-plus" /> Add Item
        </AddItemButton>
      )}

      {contributingUrls && contributingUrls.length > 0 && (() => {
        // Filter to URLs that still have items in predictedOptions
        const activeUrls = contributingUrls.filter((url) =>
          Object.values(predictedOptions || {}).some(
            (items) => Array.isArray(items) && items.some((item) => item.source_url === url)
          )
        );
        if (activeUrls.length === 0) return null;
        return (
        <div style={{ marginTop: "16px", padding: "12px", fontSize: "12px", color: "#757575", background: "#fafafa", borderRadius: "6px" }}>
          <strong>Sources:</strong>
          {activeUrls.map((url, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>
                [{i + 1}]{" "}
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
                  {url.replace(/^https?:\/\//, "")}
                </a>
              </span>
              {editable && (
                <button
                  type="button"
                  onClick={() => handleDeleteUrl(url)}
                  title="Remove this URL and all its items"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#9e9e9e",
                    fontSize: "14px",
                    padding: "0 2px",
                    lineHeight: 1,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#d32f2f")}
                  onMouseLeave={(e) => (e.target.style.color = "#9e9e9e")}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        );
      })()}
      {veganLegend && veganLegend.length > 0 && (
        <div style={{ marginTop: "8px", padding: "12px", fontSize: "12px", color: "#2E7D32", background: "#f1f8e9", borderRadius: "6px" }}>
          <strong>Vegan Legend (from menu):</strong>
          {veganLegend.map((entry, i) => (
            <div key={i}>
              <span style={{ fontWeight: "bold", marginRight: "6px" }}>{entry.marker}</span>
              — {entry.meaning}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MenuCategorizationPanel;
